import React from "react";

const CaseStudiesHero = () => {
  return (
    // Section Container: Slate background for subtle contrast
    <section className="bg-slate-50 overflow-hidden py-14 lg:py-14 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* --- Left Side: Content --- */}
          <div className="text-center lg:text-left space-y-8 max-w-2xl mx-auto lg:mx-0">
            {/* Label Badge */}
            <span className="inline-block py-1 px-3 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold tracking-wide uppercase mb-2">
              Success Stories
            </span>

            {/* Main Title */}
            <h1 className="text-4xl tracking-tight font-extrabold text-slate-900 sm:text-5xl md:text-6xl leading-[1.15]">
              Legacy System <span className="text-blue-600">Modernization</span>
            </h1>

            {/* Description */}
            <p className="text-lg text-slate-600 sm:text-xl leading-relaxed">
              Explore how we’ve helped government agencies and enterprises
              modernize their legacy infrastructure. Our case studies showcase
              real-world solutions involving custom development, secure
              integrations, and performance optimization.
            </p>

            {/* Call to Action */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-2">
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-3.5 border border-transparent text-base font-bold rounded-lg text-white bg-blue-600 hover:bg-blue-700 md:text-lg shadow-lg shadow-blue-500/30 transition-all duration-300 transform hover:-translate-y-1"
              >
                Contact Sales
              </a>
            </div>

            {/* Trust / Social Proof */}
            <div className="pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-blue-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-sm font-bold text-slate-900">
                  4.9/5 Client Satisfaction
                </span>
              </div>
              <div className="w-1 h-1 bg-slate-300 rounded-full hidden sm:block"></div>
              <span className="text-sm text-slate-500 font-medium">
                Trusted by 50+ Gov. Agencies
              </span>
            </div>
          </div>

          {/* --- Right Side: Floating Cards --- */}

          {/* Mobile View: Stacked Vertically */}
          <div className="lg:hidden flex flex-col gap-6 max-w-md mx-auto">
            {/* Card 1 */}
            <div className="relative rounded-2xl shadow-lg overflow-hidden group">
              <img
                className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-700"
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                alt="Dashboard analytics"
              />
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-blue-600 shadow-sm">
                Analytics Dashboard
              </div>
            </div>
            {/* Card 2 */}
            <div className="relative rounded-2xl shadow-lg overflow-hidden group">
              <img
                className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-700"
                src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                alt="Team collaboration"
              />
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-blue-600 shadow-sm">
                Team Collaboration
              </div>
            </div>
          </div>

          {/* Desktop View: Overlapping Floating Cards */}
          <div className="hidden lg:block relative h-[500px] w-full">
            {/* Background decoration blur */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-blue-100 rounded-full blur-3xl -z-10 opacity-50"></div>

            {/* Back Card (Top Right) */}
            <div className="absolute top-4 right-0 w-4/5 h-[340px] rounded-2xl shadow-xl overflow-hidden border-4 border-white transform translate-y-4 transition-transform duration-500 hover:-translate-y-2 hover:scale-[1.01] z-10">
              <img
                className="w-full h-full object-cover"
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                alt="Real-time Analytics"
              />
              <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-md flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Live Analytics
                </span>
              </div>
            </div>

            {/* Front Card (Bottom Left) */}
            <div className="absolute bottom-4 left-0 w-4/5 h-[340px] rounded-2xl shadow-2xl overflow-hidden border-4 border-white transform -translate-y-4 transition-transform duration-500 hover:-translate-y-6 hover:scale-[1.01] z-20">
              <img
                className="w-full h-full object-cover"
                src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                alt="Secure Communication"
              />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-md flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  ></path>
                </svg>
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Secure Comms
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesHero;
